/**
 * `Panneau` — un écran qui s'ouvre **par-dessus la chambre** (avenant A-7 décision 4).
 *
 * Bibliothèque, Arbre, Journal, Nouvelle vidéo : on ne quitte pas la chambre, on pose une
 * feuille dessus. L'Atelier, lui, reste plein écran — « on n'est plus dans la chambre, on
 * travaille » (même décision).
 *
 * Fermer = revenir à l'accueil. Échap ferme aussi : ce n'est pas un dialogue bloquant, c'est
 * une consultation.
 *
 * ⟦C44 · A-166⟧ **Compose désormais `Fenetre`** plutôt que de dessiner son propre chrome —
 * retour de Matt du 18/09/2026, capture à l'appui sur « Nouvelle vidéo » : *« on avait dit que
 * toute l'UI était en mode fenêtre […] là ça ne ressemble pas à une fenêtre »*. Le gros bouton
 * « ✕ Fermer » de l'en-tête devient la petite croix native de `Fenetre` (`onFermer`), et
 * TOUTES les pop-ups qui passent par `Panneau` (15 écrans à ce jour — Journal, Bibliothèque,
 * Arbre, Marché, Réglages, Historique, Nouvelle vidéo, Boutique, Équipe, Fil, Guide, Jalons,
 * Logement, Palmarès, Souffler) en héritent d'un coup, sans modification de leur propre code :
 * c'est tout l'intérêt d'un patron commun.
 *
 * Le piège à focus (Tab reste dans la fenêtre) est repris du patron déjà posé par `AppDialog`
 * et par la fiche « ce que ça change » (`NouvelleRun.tsx`, ⟦C21 ter · point 3⟧) — `Panneau` ne
 * le portait pas encore ; c'est le même code, à l'identique.
 */

import { useEffect, useRef, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";

import { Fenetre } from "./Fenetre";

export function Panneau({
  titre,
  large = false,
  actions,
  piedDePage,
  onClose,
  children,
}: {
  titre: string;
  /** Les panneaux de catalogue (arbre, bibliothèque) veulent plus de largeur. */
  large?: boolean;
  /**
   * ⟦C16⟧ Un bouton d'action à poser DANS le bandeau, avant la croix de fermeture — le bandeau
   * est `flex: none` dans `.fenetre` (jamais dans la zone qui défile, voir `app.css`), donc
   * tout ce qu'on y met reste visible sans défiler, structurellement, sans `position: sticky`.
   * Omis (le cas par défaut, ⟦A-10⟧ état neutre) : le bandeau ne porte que le titre et la
   * croix — Bibliothèque, Arbre et Journal ne changent pas d'un pixel.
   */
  actions?: ReactNode;
  /**
   * ⟦C44 · A-166⟧ Un pied FIXE, sous le corps qui défile — c'est lui qui garde un bouton
   * d'action (« Lancer la production », `NouvelleVideo.tsx`) visible sans défiler, à 1280×800
   * comme à 1920×1080. Omis (le cas par défaut, ⟦A-10⟧ état neutre) : pas de pied, comme avant
   * ce chantier — les 14 autres panneaux ne changent pas d'un pixel.
   */
  piedDePage?: ReactNode;
  /**
   * ⟦C17⟧ Fermer/Échap naviguent vers `/` par défaut — le comportement de toujours, valable
   * tant que le panneau est posé sur la chambre par le routeur du jeu. `Menu.tsx` pose
   * `Réglages` **hors** de ce routeur (les Paramètres sont joignables sans être en partie) : il
   * passe cette fonction pour revenir à l'écran-titre plutôt que de pousser une navigation qui
   * n'a pas de route à résoudre. Omis (le cas par défaut) : rien ne change pour les panneaux
   * existants.
   */
  onClose?: () => void;
  children: ReactNode;
}) {
  const naviguer = useNavigate();
  const fermer = onClose ?? (() => naviguer("/"));
  const boite = useRef<HTMLDivElement>(null);

  useEffect(() => {
    boite.current?.focus();
    const auClavier = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        fermer();
        return;
      }
      // ⟦C44 · A-166⟧ Piège à focus — Tab/Maj+Tab restent DANS la fenêtre, même patron
      // qu'`AppDialog` et que la fiche « ce que ça change » (`NouvelleRun.tsx`), avec un
      // correctif trouvé en vérifiant celui-ci au clavier (40 Tab, script Playwright) :
      // `:not([disabled])` — un bouton désactivé (« Lancer » avant d'avoir choisi un sujet, une
      // flèche de carrousel en bout de piste) matche `querySelectorAll("button")` mais le VRAI
      // parcours Tab du navigateur ne s'y arrête jamais. Sans le filtre, `dernier` pouvait
      // désigner un bouton désactivé que le focus n'atteint jamais : la comparaison
      // `document.activeElement === dernier` ne devenait jamais vraie, et Tab s'échappait tout
      // droit vers la chambre derrière la fenêtre (mesuré : sortie au 8ᵉ Tab, sur le curseur de
      // production value, juste avant un « Lancer » désactivé).
      if (e.key !== "Tab" || !boite.current) return;
      const focusables = boite.current.querySelectorAll<HTMLElement>(
        "button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex='-1'])",
      );
      if (focusables.length === 0) return;
      const premier = focusables[0]!;
      const dernier = focusables[focusables.length - 1]!;
      if (e.shiftKey && document.activeElement === premier) {
        e.preventDefault();
        dernier.focus();
      } else if (!e.shiftKey && document.activeElement === dernier) {
        e.preventDefault();
        premier.focus();
      }
    };
    document.addEventListener("keydown", auClavier);
    return () => document.removeEventListener("keydown", auClavier);
    // Monté une seule fois, à dessein : `fermer` change d'identité à chaque rendu (nouvelle
    // closure quand `onClose` est omis), et ni le focus initial ni l'écoute clavier n'ont
    // besoin d'être reposés pour ça — l'écouteur lit `fermer` par fermeture, la valeur la plus
    // récente est toujours celle utilisée. Pareil qu'avant ce chantier (déjà `[naviguer]`,
    // stable, mais avec le même effet pratique : un seul montage).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="panneau-fond" role="presentation">
      <div className={`panneau ${large ? "panneau-large" : ""}`} ref={boite} tabIndex={-1}>
        <Fenetre
          titre={titre}
          className="panneau-fenetre"
          role="dialog"
          ariaModal
          actions={actions}
          onFermer={fermer}
          pied={piedDePage}
        >
          {children}
        </Fenetre>
      </div>
    </div>
  );
}
